import React from "react";
import { isMobile } from "react-device-detect";
import ClickOutsideDetector from "@streeteasy/design-system/legacy/ClickOutsideDetector";
import { bool, func, node, number, oneOf, string } from "prop-types";
import { uncontrollable } from "uncontrollable";

import { KEYPRESS, POSITION, THEME, TRIGGER } from "./constants";
import { TooltipWrapper, Trigger, TriggerContainer } from "./styled";
import TooltipContent from "./tooltipContent";
import TooltipPositioner from "./tooltipPositioner";

const { BOTTOM, LEFT, RIGHT, TOP } = POSITION;
const { LARGE, SMALL } = THEME;
const { CLICK, HOVER } = TRIGGER;

export class Tooltip extends React.Component {
  static propTypes = {
    /** aria label for the button */
    "aria-label": string,
    /** Whether there's an X button in the tooltip */
    hasCloseButton: bool,
    /** Whether the tooltip is open */
    isOpen: bool,
    /** Callback function whenever the tooltip is triggered */
    onToggle: func,
    /** The position of the tooltip */
    position: oneOf([TOP, RIGHT, BOTTOM, LEFT]),
    /** Whether the tooltip should close when user clicks outside of it,
     * defaults to true */
    shouldCloseOnClickOutside: bool,
    /** The theme of the tooltip */
    theme: oneOf([LARGE, SMALL]),
    /** The trigger action for the tooltip  */
    trigger: oneOf([HOVER, CLICK]),
    /** node that will override the default icon trigger */
    triggerNode: node,
    /** value of width */
    width: string,
    /** sets the z-index of the tooltip content */
    zIndex: number
  };

  static defaultProps = {
    "aria-label": "tooltip",
    hasCloseButton: false,
    isOpen: false,
    onToggle: () => {},
    position: RIGHT,
    shouldCloseOnClickOutside: true,
    theme: LARGE,
    trigger: HOVER,
    triggerNode: <Trigger className="fa fa-question-circle" />,
    width: "",
    zIndex: 5
  };

  state = {
    updatedPosition: this.props.position
  };

  handleMouseEnterLeave = event => {
    const eventType = event.type;
    const { trigger } = this.props;

    if (!isMobile && trigger.toLowerCase() === HOVER) {
      if (eventType === "mouseenter") {
        this.triggerElem.focus();
        this.props.onToggle(true);
      }
    }
  };

  handleClick = () => {
    const { trigger } = this.props;

    if (isMobile || trigger.toLowerCase() === CLICK) {
      this.toggleTooltip();
    }
  };

  handleFocus = () => {
    const { trigger } = this.props;

    if (!isMobile && trigger.toLowerCase() === HOVER) {
      this.props.onToggle(true);
    }
  };

  handleBlur = () => {
    const { trigger } = this.props;

    if (!isMobile && trigger.toLowerCase() === HOVER) {
      this.props.onToggle(false);
    }
  };

  handleKeyPress = event => {
    event.preventDefault();

    if (event.key.toLowerCase() === KEYPRESS.ENTER) {
      this.toggleTooltip();
    }
  };

  handleKeyDown = event => {
    if (event.key.toLowerCase() === KEYPRESS.ESCAPE) {
      event.preventDefault();
      this.props.onToggle(false);
    }
  };

  toggleTooltip = () => {
    this.props.onToggle(!this.props.isOpen);
  };

  updateTooltipPosition = position => {
    this.setState(() => ({ updatedPosition: position }));
  };

  render() {
    return (
      <ClickOutsideDetector
        onClickOutside={() => {
          if (this.props.shouldCloseOnClickOutside) {
            this.props.onToggle(false);
          }
        }}
      >
        <TooltipWrapper {...this.props}>
          <TriggerContainer
            ref={el => {
              this.triggerElem = el;
            }}
            aria-label={this.props["aria-label"]}
            onBlur={this.handleBlur}
            onClick={this.handleClick}
            onFocus={this.handleFocus}
            onKeyDown={this.handleKeyDown}
            onKeyPress={this.handleKeyPress}
            onMouseEnter={this.handleMouseEnterLeave}
            onMouseLeave={this.handleMouseEnterLeave}
          >
            {this.props.triggerNode}
          </TriggerContainer>

          {this.props.isOpen && (
            <TooltipPositioner
              getTooltipElem={() => this.tooltipElem}
              getTriggerElem={() => this.triggerElem}
              position={this.props.position}
              updatePosition={this.updateTooltipPosition}
            >
              <TooltipContent
                {...this.props}
                hasCloseButton={this.props.hasCloseButton}
                onClickClose={() => this.props.onToggle(false)}
                position={this.state.updatedPosition}
                theme={this.props.theme}
                tooltipElem={el => {
                  this.tooltipElem = el;
                }}
                zIndex={this.props.zIndex}
              />
            </TooltipPositioner>
          )}
        </TooltipWrapper>
      </ClickOutsideDetector>
    );
  }
}

export default uncontrollable(Tooltip, { isOpen: "onToggle" });
